import { createContext, useContext, useSyncExternalStore } from "react";
import type { StateStore } from "oidc-client-ts";

type TerminationPhase = "active" | "ending" | "ended" | "signout_failed";

/** Bir belge ömrü içindeki çıkış geri alınmaz; yeni giriş yeni belgeye döner. */
export function createSessionTermination() {
  let phase: TerminationPhase = "active";
  let signOutRetry: (() => void) | null = null;
  const subscribers = new Set<() => void>();
  const publish = (next: TerminationPhase): void => {
    if (phase === next) return;
    phase = next;
    for (const subscriber of subscribers) subscriber();
  };
  return {
    getSnapshot: (): TerminationPhase => phase,
    subscribe: (subscriber: () => void): (() => void) => {
      subscribers.add(subscriber);
      return () => { subscribers.delete(subscriber); };
    },
    begin: (): void => { if (phase === "active") publish("ending"); },
    finish: (): void => { if (phase === "ending") publish("ended"); },
    failSignOut: (retry: () => void): void => { signOutRetry = retry; publish("signout_failed"); },
    retrySignOut: (): void => {
      if (phase !== "signout_failed" || signOutRetry === null) return;
      publish("ending");
      signOutRetry();
    },
  };
}

export type SessionTermination = ReturnType<typeof createSessionTermination>;
export const sessionTermination = createSessionTermination();
export const SessionTerminationContext = createContext(sessionTermination);

export function useSessionTermination(): SessionTermination {
  return useContext(SessionTerminationContext);
}

export function useSessionTerminationPhase(): TerminationPhase {
  const termination = useSessionTermination();
  return useSyncExternalStore(termination.subscribe, termination.getSnapshot, termination.getSnapshot);
}

/**
 * stopSilentRenew başlamış yenilemeyi iptal etmez. Eski refresh yanıtının
 * storeUser yazısı, removeUser sonrasında bile gelebilir; yalnız kullanıcı
 * deposunu kapatırız. OIDC yönlendirme state deposu çalışmaya devam eder.
 */
export function guardTerminatedUserStore(store: StateStore, termination: SessionTermination = sessionTermination): StateStore {
  return {
    get: key => termination.getSnapshot() === "active" ? store.get(key) : Promise.resolve(null),
    set: (key, value) => termination.getSnapshot() === "active" ? store.set(key, value) : Promise.resolve(),
    remove: key => store.remove(key),
    getAllKeys: () => store.getAllKeys(),
  };
}
